import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from '@/hooks/useInView';
import {
  Shield, Cloud, Lock, Key, Eye, Server,
  Database, RefreshCw, Users, CheckCircle,
  ArrowRight, ArrowDown, ChevronDown, ChevronUp, Check,
  Layers, Globe, HardDrive, ShieldCheck,
  FileCheck, Fingerprint, MonitorCheck, Wifi,
  Smartphone, KeyRound
} from 'lucide-react';
import BookDemoDialog from '@/components/BookDemoDialog';

const RED = '#E02D45';
const GREEN = '#34D399';

function Section({ children, className = '', id }) {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} id={id} className={`${inView ? 'animate-fade-in-up' : 'opacity-0'} ${className}`}>
      {children}
    </section>
  );
}

function TypewriterLine({ text, visible, startDelay = 0, highlightColor = null }) {
  return (
    <div className="overflow-hidden">
      <span className="inline-block">
        {text.split('').map((char, i) => (
          <span
            key={`${char}-${i}`}
            className={`inline-block typewriter-char ${visible ? 'typewriter-char-visible' : ''}`}
            style={{ transitionDelay: `${startDelay + i * 70}ms`, color: highlightColor || undefined }}
          >
            {char === ' ' ? '\u00A0' : char}
          </span>
        ))}
      </span>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   DATA
   ═══════════════════════════════════════════════════════ */

const trustMetrics = [
  { value: 'AWS', label: 'Cloud Foundation', desc: 'Enterprise-grade infrastructure' },
  { value: 'AES-256', label: 'Encryption Standard', desc: 'Data at rest and in transit' },
  { value: 'RBAC', label: 'Access Control', desc: 'Role-based permissions' },
  { value: '99.9%', label: 'Uptime Target', desc: 'Reliable cloud availability' },
];

const cloudFeatures = [
  { icon: Server, title: 'AWS Cloud Infrastructure', desc: 'Mushin is hosted on Amazon Web Services, a trusted, enterprise-grade cloud platform used by the world\'s largest enterprises, banks, and manufacturers. Your data runs on infrastructure built for scale, security, and performance.' },
  { icon: Globe, title: 'Scalable Multi-Plant Access', desc: 'Cloud deployment enables secure access from any authorized location, whether it\'s a single plant or a multi-plant group. Scale seamlessly as your operations grow, without infrastructure bottlenecks.' },
  { icon: MonitorCheck, title: 'High Availability & Uptime', desc: 'Mushin is designed for reliable availability so your teams can access plant data when they need it. Cloud architecture ensures minimal downtime and consistent access for daily operations.' },
  { icon: Wifi, title: 'Remote Access for Authorized Teams', desc: 'Authorized plant heads, quality managers, and IT teams can securely access dashboards, reports, and workflows from any location. Real-time visibility without compromising control.' },
];

const securityPillars = [
  { icon: Lock, title: 'Data Encryption', desc: 'All sensitive data is encrypted both at rest and in transit using industry-standard AES-256 encryption. Your quality records, traceability data, and plant information are protected at every layer.', points: ['AES-256 encryption at rest', 'TLS/SSL encrypted data transmission', 'Secure API communication', 'Encrypted database storage'] },
  { icon: Key, title: 'Role-Based Access Control', desc: 'Access to Mushin is managed through a structured role-based permission system. Every user sees only what they are authorized to see, by role, department, plant, or function.', points: ['User-level permission control', 'Department and plant-level access', 'Function-based visibility', 'Admin-controlled user management'] },
  { icon: Eye, title: 'Audit Trail & Accountability', desc: 'Every action in Mushin is logged and traceable. Approvals, edits, reviews, and workflows create a structured audit trail that supports internal governance and external audit readiness.', points: ['Timestamped action logging', 'Approval chain visibility', 'Change history records', 'Audit-ready evidence trails'] },
  { icon: Fingerprint, title: 'Secure Authentication & MFA', desc: 'Mushin supports secure authentication with multi-factor authentication (MFA / 2FA) capability, adding an extra verification step beyond passwords to protect user accounts and sensitive plant data.', points: ['Multi-factor authentication (MFA / 2FA)', 'Secure login protocols', 'Session management controls', 'Password policy enforcement'] },
];

const reliabilityFeatures = [
  { icon: Database, title: 'Regular Data Backups', desc: 'Mushin maintains structured backup schedules to ensure your operational data is protected. Backups are stored securely and designed for reliable recovery when needed.' },
  { icon: RefreshCw, title: 'Business Continuity', desc: 'Cloud architecture provides built-in resilience. Your plant data and quality records are protected against disruption, ensuring continuity for daily operations.' },
  { icon: HardDrive, title: 'Secure Data Storage', desc: 'All manufacturing data, including quality records, traceability logs, and audit evidence, is stored in secure, managed cloud databases with redundancy and protection built in.' },
  { icon: FileCheck, title: 'Data Integrity Assurance', desc: 'Mushin ensures the integrity of your operational records through structured storage, validation checks, and controlled access. Your data remains accurate and trustworthy.' },
];

const deploymentBlocks = [
  { title: 'Client Data Ownership', desc: 'Your operational data belongs to you. Mushin provides secure access to your plant records, quality evidence, and operational data. Structured and available for authorized use at all times.' },
  { title: 'Controlled Deployment', desc: 'Every Mushin deployment follows a structured rollout process. Configuration, access setup, and user permissions are established during onboarding to ensure a secure, controlled launch.' },
  { title: 'Trusted Cloud Foundation', desc: 'Built on AWS. The same infrastructure trusted by global enterprises, financial institutions, and critical industrial platforms. Your data sits on a foundation designed for reliability and security.' },
  { title: 'Multi-User Governance', desc: 'Plant heads, quality managers, IT teams, and operators each get controlled access to their relevant workflows and data. Visibility is structured, not open, not siloed, but governed.' },
  { title: 'Industrial Data Discipline', desc: 'Mushin is purpose-built for manufacturing data. Quality checksheets, traceability records, CAPA logs, and supplier data are handled with the discipline that sensitive plant operations require.' },
  { title: 'Integration-Ready Architecture', desc: 'Mushin supports structured integration discussions for enterprise customers. ERP connectivity, IoT data flows, and custom deployment configurations based on your operational needs.' },
];

const rbacRoles = [
  { role: 'Plant Head / Management', access: 'Dashboards, analytics, MIS reports, audit readiness overviews, multi-department view', level: 'Strategic', color: RED },
  { role: 'Quality Manager / HOD', access: 'Quality workflows, CAPA management, compliance tracking, standards, approval authority', level: 'Operational', color: '#F59E0B' },
  { role: 'Department Lead / Supervisor', access: 'Department checksheets, team assignments, daily reports, production records', level: 'Supervisory', color: '#38BDF8' },
  { role: 'Operator / Inspector', access: 'Assigned checksheets, inspection forms, data entry, task completion', level: 'Execution', color: GREEN },
  { role: 'IT / System Admin', access: 'User management, permissions, system configuration, integration settings', level: 'Administration', color: '#A78BFA' },
];

const faqItems = [
  { q: 'Does Mushin support multi-factor authentication (MFA / 2FA)?', a: 'Yes. Mushin supports multi-factor authentication to add an extra layer of login security beyond passwords. With MFA / 2FA, users verify their identity through a second step, such as a one-time code, before accessing the platform. This helps protect sensitive plant and quality data against unauthorized access, even if a password is compromised.' },
  { q: 'Where is Mushin hosted?', a: 'Mushin is hosted on Amazon Web Services (AWS), a globally trusted, enterprise-grade cloud platform. AWS infrastructure provides the security, reliability, and scalability that manufacturing operations require.' },
  { q: 'How is our data protected?', a: 'All data is encrypted using AES-256 encryption at rest and TLS/SSL encryption in transit. Access is controlled through role-based permissions, and every action is logged for audit readiness. Your quality records and plant data are protected at every layer.' },
  { q: 'Is access controlled by role?', a: 'Yes. Mushin uses a structured role-based access control (RBAC) system. Each user is assigned permissions based on their role, department, plant, and function, ensuring they can only view and act on information relevant to their responsibilities.' },
  { q: 'Can cloud deployment be trusted for manufacturing data?', a: 'Absolutely. Cloud infrastructure like AWS is trusted by the world\'s largest enterprises, banks, and manufacturers for handling sensitive operational data. Cloud deployment provides better security, reliability, and disaster recovery than most on-premises setups.' },
  { q: 'How does Mushin help reduce data risk?', a: 'Through encrypted storage, regular backups, role-based access, audit trails, and secure authentication. Every layer of Mushin is designed to protect your data from unauthorized access, loss, or corruption.' },
  { q: 'Can Mushin support multi-plant access securely?', a: 'Yes. Mushin\'s cloud architecture supports secure multi-plant deployment. Each plant can have its own access controls, user roles, and data visibility, while central management teams can view consolidated dashboards and reports across locations.' },
  { q: 'Who can access our data inside Mushin?', a: 'Only authorized users within your organization. Access is governed by roles and permissions set by your admin team. Mushin\'s support team follows strict access protocols and only assists with system support when authorized.' },
  { q: 'What happens if something goes wrong?', a: 'Mushin maintains regular data backups and operates on resilient cloud infrastructure designed for business continuity. In the rare event of disruption, structured recovery procedures are in place to restore access and data quickly.' },
];

/* ═══════════════════════════════════════════════════════
   PAGE
   ═══════════════════════════════════════════════════════ */

export default function SecurityPage() {
  const [demoOpen, setDemoOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [heroVisible, setHeroVisible] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setHeroVisible(true), 500);
    return () => clearTimeout(t);
  }, [setHeroVisible]);

  return (
    <div data-testid="security-page">
      {/* ═══ 1. HERO ═══ */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-28 bg-[#060606] overflow-hidden" data-testid="security-hero">
        <div className="absolute inset-0 opacity-[0.04]" style={{
          backgroundImage: `linear-gradient(${RED}30 1px, transparent 1px), linear-gradient(90deg, ${RED}30 1px, transparent 1px)`,
          backgroundSize: '80px 80px'
        }} />
        <div className="absolute top-0 right-0 w-[600px] h-[400px] rounded-full blur-[200px]" style={{ backgroundColor: `${RED}06` }} />

        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 relative z-10">
          <div className={`transition-all duration-[1200ms] ease-out ${heroVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '100ms' }}>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: RED }} />
              <p className="text-xs font-bold uppercase tracking-[0.25em]" style={{ color: RED }}>Security & Deployment</p>
            </div>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-[4.5rem] font-black uppercase tracking-[-0.03em] text-white leading-[0.92] max-w-4xl" data-testid="security-hero-title">
            <TypewriterLine text="Enterprise-Grade" visible={heroVisible} startDelay={400} highlightColor={RED} />
            <TypewriterLine text="Security for" visible={heroVisible} startDelay={1600} />
            <TypewriterLine text="Factory Operations" visible={heroVisible} startDelay={2500} />
          </h1>

          <div className={`transition-all duration-[1400ms] ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '4200ms' }}>
            <div className="mt-10 h-[3px] w-16 hero-accent" style={{ backgroundColor: RED, animationDelay: '4300ms' }} />
            <p className="mt-8 text-lg text-white/50 max-w-2xl leading-[1.7] font-medium">
              <span className="text-white font-bold">MUSHIN</span> is built on trusted cloud infrastructure with encrypted data handling, controlled access, and enterprise-ready deployment. Designed to protect your sensitive plant data with confidence.
            </p>
          </div>

          <div className={`mt-10 flex flex-wrap gap-4 transition-all duration-[1400ms] ease-out ${heroVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`} style={{ transitionDelay: '4600ms' }}>
            <Link to="/contact" className="text-white px-8 py-3.5 rounded-full font-bold text-sm inline-flex items-center gap-2 transition-all duration-300 hover:shadow-xl" style={{ backgroundColor: RED }} data-testid="security-book-demo">
              Talk to Us About Secure Deployment <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="mailto:sales@mushinlabs.com" className="border-2 border-white/15 hover:border-white/30 text-white/50 hover:text-white px-8 py-3.5 rounded-full font-bold text-sm transition-all duration-300" data-testid="security-get-quote">
              Get a Quote
            </a>
          </div>
        </div>

        <div className={`absolute bottom-6 left-1/2 -translate-x-1/2 transition-all duration-1000 ${heroVisible ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '5200ms' }}>
          <ArrowDown className="w-5 h-5 text-white/15 animate-bounce" />
        </div>
      </section>

      {/* ═══ 2. TRUST METRICS ═══ */}
      <Section className="py-10 lg:py-14 bg-[#0A0A0A] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
            {trustMetrics.map((m) => (
              <div key={m.label} className="text-center py-4" data-testid={`trust-metric-${m.label}`}>
                <div className="text-3xl sm:text-4xl font-black text-white uppercase tracking-tight">{m.value}</div>
                <div className="text-sm font-bold mt-2" style={{ color: RED }}>{m.label}</div>
                <div className="text-[13px] text-white/30 mt-1 font-medium">{m.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ═══ 3. CLOUD INFRASTRUCTURE ═══ */}
      <Section className="py-20 lg:py-28 bg-[#060606] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>Cloud Infrastructure</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              Secure Cloud Deployment for Manufacturing
            </h2>
            <p className="mt-5 text-base text-white/40 max-w-xl leading-[1.7] font-medium">
              Mushin is deployed on Amazon Web Services, providing the scalability, reliability, and security that enterprise manufacturing operations demand.
            </p>
          </div>

          {/* Cloud Architecture Diagram */}
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6 sm:p-10 mb-10" data-testid="cloud-architecture-diagram">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/20 mb-8 text-center">Mushin Cloud Architecture</p>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-center">
              {/* User Layer */}
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3 border" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                  <Users className="w-7 h-7" style={{ color: RED }} strokeWidth={1.7} />
                </div>
                <p className="text-xs font-bold text-white/80">Authorized Users</p>
                <p className="text-[10px] text-white/30 mt-0.5">Plant teams, managers, admins</p>
              </div>
              {/* Arrow */}
              <div className="hidden md:flex items-center justify-center">
                <div className="flex items-center gap-1.5">
                  <div className="w-6 h-[2px]" style={{ backgroundColor: `${RED}40` }} />
                  <span className="text-[9px] font-bold uppercase tracking-wider text-white/20">TLS / SSL</span>
                  <div className="w-6 h-[2px]" style={{ backgroundColor: `${RED}40` }} />
                  <ArrowRight className="w-3.5 h-3.5" style={{ color: RED }} />
                </div>
              </div>
              {/* Mobile connector */}
              <div className="md:hidden flex justify-center">
                <div className="flex flex-col items-center gap-1">
                  <div className="w-[2px] h-3" style={{ backgroundColor: `${RED}40` }} />
                  <span className="text-[8px] font-bold uppercase tracking-wider text-white/20">TLS / SSL</span>
                  <div className="w-[2px] h-3" style={{ backgroundColor: `${RED}40` }} />
                </div>
              </div>
              {/* App Layer */}
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3 border" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                  <Layers className="w-7 h-7" style={{ color: RED }} strokeWidth={1.7} />
                </div>
                <p className="text-xs font-bold text-white/80">Mushin Platform</p>
                <p className="text-[10px] text-white/30 mt-0.5">RBAC, workflows, audit trails</p>
              </div>
              {/* Arrow */}
              <div className="hidden md:flex items-center justify-center">
                <div className="flex items-center gap-1.5">
                  <div className="w-6 h-[2px]" style={{ backgroundColor: `${RED}40` }} />
                  <span className="text-[9px] font-bold uppercase tracking-wider text-white/20">AES-256</span>
                  <div className="w-6 h-[2px]" style={{ backgroundColor: `${RED}40` }} />
                  <ArrowRight className="w-3.5 h-3.5" style={{ color: RED }} />
                </div>
              </div>
              {/* Mobile connector */}
              <div className="md:hidden flex justify-center">
                <div className="flex flex-col items-center gap-1">
                  <div className="w-[2px] h-3" style={{ backgroundColor: `${RED}40` }} />
                  <span className="text-[8px] font-bold uppercase tracking-wider text-white/20">AES-256</span>
                  <div className="w-[2px] h-3" style={{ backgroundColor: `${RED}40` }} />
                </div>
              </div>
              {/* Infrastructure Layer */}
              <div className="flex flex-col items-center text-center">
                <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-3 border" style={{ backgroundColor: '#F5920012', borderColor: '#F5920020' }}>
                  <Cloud className="w-7 h-7 text-[#F59200]" strokeWidth={1.7} />
                </div>
                <p className="text-xs font-bold text-white/80">AWS Infrastructure</p>
                <p className="text-[10px] text-white/30 mt-0.5">Encrypted storage, backups</p>
              </div>
            </div>
          </div>

          {/* Cloud Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {cloudFeatures.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-6 hover:border-[#E02D45]/20 transition-all duration-300" data-testid={`cloud-feature-${f.title}`}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 border" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                    <Icon className="w-5 h-5" style={{ color: RED }} strokeWidth={1.7} />
                  </div>
                  <h3 className="text-sm font-bold text-white/80 uppercase">{f.title}</h3>
                  <p className="mt-2 text-[13px] text-white/35 leading-relaxed font-medium">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ═══ 4. DATA SECURITY PILLARS ═══ */}
      <Section className="py-20 lg:py-28 bg-[#0A0A0A] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>Data Security</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              Your Operational Data Is Protected at Every Layer
            </h2>
            <p className="mt-5 text-base text-white/40 max-w-xl leading-[1.7] font-medium">
              From encrypted storage to role-based permissions, every aspect of Mushin is designed to keep your manufacturing data secure, controlled, and audit-ready.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {securityPillars.map((pillar) => {
              const Icon = pillar.icon;
              return (
                <div key={pillar.title} className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-7 hover:border-[#E02D45]/15 transition-all duration-300" data-testid={`security-pillar-${pillar.title}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 border" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                      <Icon className="w-5 h-5" style={{ color: RED }} strokeWidth={1.7} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-white/85 uppercase tracking-tight">{pillar.title}</h3>
                      <p className="mt-2 text-[13px] text-white/35 leading-relaxed font-medium">{pillar.desc}</p>
                    </div>
                  </div>
                  <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-2 pl-16">
                    {pillar.points.map((point) => (
                      <div key={point} className="flex items-center gap-2">
                        <Check className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0" strokeWidth={2.5} />
                        <span className="text-[12px] text-white/45 font-medium">{point}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ═══ 5. ENCRYPTED DATA FLOW ═══ */}
      <Section className="py-20 lg:py-28 bg-[#060606] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>Encryption & Privacy</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              Encrypted Data Handling from End to End
            </h2>
            <p className="mt-5 text-base text-white/40 max-w-xl leading-[1.7] font-medium">
              Sensitive manufacturing records, including quality checksheets, traceability logs, supplier data, and audit evidence, are encrypted and protected through every stage of handling.
            </p>
          </div>

          {/* Encryption Flow Diagram */}
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 sm:p-10" data-testid="encryption-flow-diagram">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/20 mb-8 text-center">Secure Data Flow</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { icon: Users, step: '01', title: 'User Action', desc: 'Authorized user submits data through secured application interface', detail: 'Authenticated session' },
                { icon: Lock, step: '02', title: 'Encrypted Transmission', desc: 'Data is encrypted using TLS/SSL during transfer to cloud servers', detail: 'TLS 1.2+ protocol' },
                { icon: ShieldCheck, step: '03', title: 'Access Validation', desc: 'Role-based permissions verify user authorization before processing', detail: 'RBAC enforcement' },
                { icon: Database, step: '04', title: 'Encrypted Storage', desc: 'Data is stored in AES-256 encrypted database with secure backups', detail: 'AES-256 at rest' },
              ].map((item, idx) => {
                const Icon = item.icon;
                return (
                  <div key={item.step} className="relative">
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] mb-3" style={{ color: RED }}>Step {item.step}</div>
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-4 border" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                      <Icon className="w-5 h-5 text-white/70" strokeWidth={1.7} />
                    </div>
                    <h4 className="text-sm font-bold text-white/80 uppercase">{item.title}</h4>
                    <p className="mt-2 text-[12px] text-white/30 leading-relaxed font-medium">{item.desc}</p>
                    <div className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/[0.03] border border-white/[0.06]">
                      <Lock className="w-2.5 h-2.5" style={{ color: RED }} />
                      <span className="text-[10px] font-bold text-white/30 uppercase tracking-wider">{item.detail}</span>
                    </div>
                    {idx < 3 && <div className="hidden lg:block absolute top-14 -right-3 w-6 h-[2px]" style={{ backgroundColor: `${RED}30` }} />}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </Section>

      {/* ═══ 6. MFA / 2FA ═══ */}
      <Section className="py-20 lg:py-28 bg-[#0A0A0A] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left - Content */}
            <div>
              <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>Identity & Login Security</p>
              <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
                Multi-Factor Authentication for Stronger Access Control
              </h2>
              <p className="mt-6 text-base text-white/40 leading-[1.7] font-medium max-w-lg">
                Mushin supports Multi-Factor Authentication (MFA / 2FA), adding an extra verification step beyond passwords to protect user accounts. This helps ensure that only verified users can access sensitive plant data, quality records, and operational workflows.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  { title: 'Extra layer of login security', desc: 'Users verify their identity through a second step before accessing the platform, reducing the risk of unauthorized access.' },
                  { title: 'Protection for sensitive plant data', desc: 'Quality records, traceability logs, and supplier data are safeguarded behind stronger authentication controls.' },
                  { title: 'Enterprise-ready account protection', desc: 'MFA / 2FA helps meet enterprise security expectations for multi-user deployments across plants and teams.' },
                  { title: 'Confidence for IT and management teams', desc: 'Gives IT administrators and plant leadership greater confidence that user access is properly controlled and verified.' },
                ].map((item) => (
                  <div key={item.title} className="flex items-start gap-3" data-testid={`mfa-benefit-${item.title}`}>
                    <Check className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" strokeWidth={2.5} />
                    <div>
                      <span className="text-sm font-bold text-white/80">{item.title}</span>
                      <p className="text-[13px] text-white/35 mt-0.5 leading-relaxed font-medium">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right - MFA Login Flow */}
            <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 sm:p-8" data-testid="mfa-flow-diagram">
              <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/20 mb-6 text-center">Secure Login Flow with MFA</p>
              <div className="space-y-5">
                {/* Step 1 */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                    <Key className="w-4 h-4 text-white/70" strokeWidth={1.7} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: RED }}>Step 1</span>
                      <span className="text-sm font-bold text-white/80">Enter Credentials</span>
                    </div>
                    <p className="text-[12px] text-white/30 font-medium">User enters their username and password to initiate login</p>
                    <div className="mt-2 bg-white/[0.02] border border-white/[0.06] rounded-lg px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-full h-2 bg-white/[0.06] rounded-full" />
                        <Lock className="w-3 h-3 text-white/15" />
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <div className="w-full h-2 bg-white/[0.06] rounded-full" />
                        <Eye className="w-3 h-3 text-white/15" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Connector */}
                <div className="flex items-center pl-5">
                  <div className="w-[2px] h-5 ml-[18px]" style={{ backgroundColor: `${RED}30` }} />
                </div>

                {/* Step 2 */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: RED }}>
                    <Smartphone className="w-4 h-4 text-white" strokeWidth={1.7} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: RED }}>Step 2</span>
                      <span className="text-sm font-bold text-white/80">MFA Verification</span>
                    </div>
                    <p className="text-[12px] text-white/30 font-medium">Second verification step confirms identity before granting access</p>
                    <div className="mt-2 bg-white/[0.02] border rounded-lg px-4 py-3 flex items-center" style={{ borderColor: `${RED}20` }}>
                      <div className="flex items-center gap-3">
                        <KeyRound className="w-4 h-4" style={{ color: RED }} />
                        <div className="flex gap-1.5">
                          {[4,5,6,'*','*','*'].map((n, idx) => (
                            <div key={`otp-${idx}`} className="w-5 h-7 rounded flex items-center justify-center border" style={{ backgroundColor: `${RED}08`, borderColor: `${RED}15` }}>
                              <span className="text-xs font-mono font-bold" style={{ color: RED }}>{n}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Connector */}
                <div className="flex items-center pl-5">
                  <div className="w-[2px] h-5 ml-[18px]" style={{ backgroundColor: `${GREEN}30` }} />
                </div>

                {/* Step 3 */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: GREEN }}>
                    <ShieldCheck className="w-4 h-4 text-white" strokeWidth={1.7} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold uppercase tracking-[0.2em]" style={{ color: GREEN }}>Verified</span>
                      <span className="text-sm font-bold text-white/80">Secure Access Granted</span>
                    </div>
                    <p className="text-[12px] text-white/30 font-medium">Identity confirmed. User accesses authorized data and workflows.</p>
                    <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-full border" style={{ backgroundColor: `${GREEN}08`, borderColor: `${GREEN}20` }}>
                      <CheckCircle className="w-3 h-3" style={{ color: GREEN }} />
                      <span className="text-[10px] font-bold" style={{ color: GREEN }}>Access verified and secured</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* ═══ 7. BACKUP & RELIABILITY ═══ */}
      <Section className="py-20 lg:py-28 bg-[#060606] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: GREEN }}>Backup & Reliability</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              Designed for Continuity and Reliability
            </h2>
            <p className="mt-5 text-base text-white/40 max-w-xl leading-[1.7] font-medium">
              Built to reduce operational data risk. Structured backup processes, resilient infrastructure, and secure data storage ensure your plant data is always protected and recoverable.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {reliabilityFeatures.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-6 flex items-start gap-4 hover:border-[#34D399]/20 transition-all duration-300" data-testid={`reliability-feature-${f.title}`}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 border" style={{ backgroundColor: `${GREEN}10`, borderColor: `${GREEN}20` }}>
                    <Icon className="w-5 h-5" style={{ color: GREEN }} strokeWidth={1.7} />
                  </div>
                  <div>
                    <h3 className="text-sm font-bold text-white/80 uppercase">{f.title}</h3>
                    <p className="mt-2 text-[13px] text-white/35 leading-relaxed font-medium">{f.desc}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      {/* ═══ 8. DEPLOYMENT CONFIDENCE ═══ */}
      <Section className="py-20 lg:py-28 bg-[#0A0A0A] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>Deployment Confidence</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              Security and Deployment Confidence for Industrial Teams
            </h2>
            <p className="mt-5 text-base text-white/40 max-w-xl leading-[1.7] font-medium">
              Mushin is built to address real concerns that plant heads, IT teams, and quality leaders have about cloud deployment and data handling for manufacturing operations.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {deploymentBlocks.map((block, i) => (
              <div key={block.title} className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-6 hover:border-[#E02D45]/15 transition-all duration-300" data-testid={`deployment-block-${block.title}`}>
                <div className="w-8 h-8 rounded-lg flex items-center justify-center mb-4 border" style={{ backgroundColor: `${RED}10`, borderColor: `${RED}20` }}>
                  <span className="text-xs font-bold font-mono" style={{ color: RED }}>{String(i + 1).padStart(2, '0')}</span>
                </div>
                <h3 className="text-sm font-bold text-white/80 uppercase mb-2">{block.title}</h3>
                <p className="text-[13px] text-white/35 leading-relaxed font-medium">{block.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ═══ 9. ROLE-BASED ACCESS ═══ */}
      <Section className="py-20 lg:py-28 bg-[#060606] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mb-14">
            <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>Access Control</p>
            <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
              Controlled Visibility by Role and Responsibility
            </h2>
            <p className="mt-5 text-base text-white/40 max-w-xl leading-[1.7] font-medium">
              Every user in Mushin sees exactly what they need. Nothing more, nothing less. Access is governed by role, department, plant, and function.
            </p>
          </div>

          {/* RBAC Diagram */}
          <div className="bg-white/[0.03] border border-white/[0.06] rounded-2xl p-6 sm:p-10" data-testid="rbac-diagram">
            <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-white/20 mb-8 text-center">Role-Based Access Structure</p>
            <div className="space-y-4">
              {rbacRoles.map((item, i) => (
                <div key={item.role} className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5" data-testid={`rbac-role-${item.role}`}>
                  <div className="sm:w-56 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: item.color }} />
                      <span className="text-sm font-bold text-white/80">{item.role}</span>
                    </div>
                    <span className="text-[10px] font-bold uppercase tracking-[0.15em] ml-[18px]" style={{ color: item.color }}>{item.level}</span>
                  </div>
                  <div className="flex-1">
                    <div className="rounded-lg px-4 py-2.5 border" style={{ backgroundColor: `${item.color}08`, borderColor: `${item.color}15`, maxWidth: `${100 - i * 8}%` }}>
                      <p className="text-[12px] text-white/45 leading-relaxed font-medium">{item.access}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ═══ 10. FAQ ═══ */}
      <Section className="py-20 lg:py-28 bg-[#0A0A0A] border-t border-white/[0.04]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-14">
              <p className="text-sm font-bold uppercase tracking-[0.25em] mb-6" style={{ color: RED }}>Frequently Asked Questions</p>
              <h2 className="text-3xl sm:text-4xl font-black uppercase tracking-[-0.02em] text-white leading-[1.05]">
                Common Questions About Security and Deployment
              </h2>
            </div>

            <div className="space-y-3">
              {faqItems.map((item, i) => (
                <div key={item.q} className={`border rounded-xl overflow-hidden transition-colors duration-300 ${openFaq === i ? 'border-[#E02D45]/20 bg-white/[0.02]' : 'border-white/[0.06] bg-white/[0.02]'}`} data-testid={`faq-item-${i}`}>
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between px-6 py-4 text-left">
                    <span className="text-sm font-bold text-white/75 pr-4">{item.q}</span>
                    {openFaq === i
                      ? <ChevronUp className="w-4 h-4 flex-shrink-0" style={{ color: RED }} />
                      : <ChevronDown className="w-4 h-4 text-white/20 flex-shrink-0" />
                    }
                  </button>
                  {openFaq === i && (
                    <div className="px-6 pb-5">
                      <p className="text-[13px] text-white/40 leading-relaxed font-medium">{item.a}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ═══ 11. FINAL CTA ═══ */}
      <section className="py-28 lg:py-40 relative overflow-hidden bg-[#060606] border-t border-white/[0.04]" data-testid="security-cta-section">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full blur-[150px]" style={{ backgroundColor: `${RED}06` }} />
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 text-center relative z-10">
          <p className="text-sm font-bold uppercase tracking-[0.25em] mb-8" style={{ color: RED }}>Get Started</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-[-0.02em] text-white leading-[1]">
            Ready to Discuss Secure<br />Deployment for Your Plant?
          </h2>
          <p className="mt-6 text-white/35 text-lg max-w-lg mx-auto font-medium leading-[1.7]">
            Talk to the <span className="text-white font-bold">MUSHIN</span> team about enterprise-ready security, deployment planning, and how we protect sensitive manufacturing data.
          </p>
          <div className="mt-10 flex flex-wrap gap-4 justify-center">
            <Link to="/contact" className="text-white px-10 py-4 rounded-full font-bold text-base inline-flex items-center gap-2.5 transition-all duration-300 hover:shadow-xl" style={{ backgroundColor: RED }} data-testid="security-cta-demo">
              Book a Demo <ArrowRight className="w-5 h-5" />
            </Link>
            <a href="mailto:sales@mushinlabs.com" className="border-2 border-white/15 hover:border-white/30 text-white/45 hover:text-white px-10 py-4 rounded-full font-bold text-base transition-all duration-300" data-testid="security-cta-quote">
              Talk to Sales
            </a>
          </div>
        </div>
      </section>

      <BookDemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}
